import { type To, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { getBottomNavItems, isNavItemActive } from '@/config/navigation';
import { useAuth } from '@/hooks/useAuth';

import { useNavSection } from './useNavSection';

export const useNavigation = (
  userRole: string,
  handleDrawerToggle: () => void
) => {
  const location = useLocation();
  const section = useNavSection(location.pathname, location.search);
  const navigate = useNavigate();
  const { loginWithRedirect, logout, isAuthenticated } = useAuth0();
  const { user } = useAuth();

  const getBottomNavValue = (): number | false => {
    const bottomNavItems = getBottomNavItems(userRole, user);

    for (let i = 0; i < bottomNavItems.length; i++) {
      const item = bottomNavItems[i];
      if (
        'path' in item &&
        isNavItemActive(location.pathname, item.path, section)
      ) {
        return i;
      }
    }

    return false;
  };

  const handleBottomNavChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    const bottomNavItems = getBottomNavItems(userRole, user);
    const selectedItem = bottomNavItems[newValue];

    if (!selectedItem) return;

    // Handle More menu (opens drawer)
    if (selectedItem.id === 'more') {
      handleDrawerToggle();
      return;
    }

    // Navigate to selected route (include search when present, e.g. /class?inApp=1)
    if ('path' in selectedItem && selectedItem.path) {
      const to: To =
        'search' in selectedItem && selectedItem.search
          ? { pathname: selectedItem.path, search: selectedItem.search }
          : selectedItem.path;
      // Router state tells a page it was opened from the nav (Students).
      if ('state' in selectedItem && selectedItem.state) {
        navigate(to, { state: selectedItem.state });
      } else {
        navigate(to);
      }
    }
  };

  const handleAuthAction = (
    action: 'login' | 'logout',
    onComplete?: () => void
  ) => {
    if (action === 'login') {
      loginWithRedirect();
    } else if (action === 'logout') {
      logout({ returnTo: window.location.origin } as Parameters<
        typeof logout
      >[0]);
    }

    if (onComplete) {
      onComplete();
    }
  };

  return {
    location,
    navigate,
    isAuthenticated,
    getBottomNavValue,
    handleBottomNavChange,
    handleAuthAction,
  };
};
